import React, {useState} from "react";
import {Button, CheckBoxField, SelectField, TextArea, TextField, useInputForm} from "../../../../Components/Inputs";
import {GridCell} from "../../../../Components/Grid";
import {braillerRepairRequestModel} from "./braillerRepairRequestFactory";
import {BRAILLER_REPLACEMENT_STATUS, BRAILLER_REPLACEMENT_STATUS_LIST} from "../braillerConstants";
import {LinkButton} from "../../../../Components/Link";
import {locations} from "../../../../Utilities/Location";
import type {ListItem} from "../../../../Utilities/Types/listUtilities";

interface BraillerRepairRequestViewProps {
    catalogItem: Record<string, any>;
    loanedBraillers: Record<string, any>[];
    user: Record<string, any>;
    onSubmit: (data: Record<string, any>) => void;
    onCancel: () => void;
    studentId: string | null;
    onStudentChange: (id: string) => void;
    students: ListItem[];
    isFimcAdmin: unknown;
    shippingLocations: ListItem[];
    shippingLocationId: string | null;
    onShippingLocationChange: (id: string | null) => void;
    setWarnings: (warnings: string[]) => void;
}

export const BraillerRepairRequestView = ({
                                              catalogItem,
                                              loanedBraillers,
                                              user,
                                              onSubmit,
                                              onCancel,
                                              studentId,
                                              onStudentChange,
                                              students,
                                              isFimcAdmin,
                                              shippingLocations,
                                              shippingLocationId,
                                              onShippingLocationChange,
                                              setWarnings
                                          }: BraillerRepairRequestViewProps) => {
    const [manualSerialNumber, setManualSerialNumber] = useState("");

    const repairForm = useInputForm(braillerRepairRequestModel());

    const loanOptions = loanedBraillers.map((loan: Record<string, any>) => ({
        value: loan.braillerLoanId,
        text: `${loan.catalogItemTitle} - ${loan.inventoryBarCode}`,
    }));

    loanOptions.push({value: "manual", text: "Brailler not found - Enter serial number manually"});

    const handleNoSerialNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = e.target.checked;
        repairForm.noSerialNumber.setValue(String(isChecked));
        
        if (isChecked) {
            setManualSerialNumber("");
        }
    };

    const isReplacementNeeded = () => {
        const replacementValue = parseInt(repairForm.replacementPreference.value, 10);
        return replacementValue !== BRAILLER_REPLACEMENT_STATUS.NotNeeded.id;
    };

    const handleSubmit = () => {
        // Validate shipping location if replacement is needed
        if (isReplacementNeeded() && !shippingLocationId) {
            setWarnings(["Shipping Location is required when a replacement preference is selected."]);
            return;
        }

        const formData = {
            braillerCatalogItemId: catalogItem.braillerCatalogId || catalogItem.BraillerCatalogId || catalogItem.id,
            serialNumber: repairForm.noSerialNumber.value ? "" : manualSerialNumber, 
            issue: repairForm.issue.value,
            replacementPreference: parseInt(repairForm.replacementPreference.value, 10),
            reportedBy: user.fullName || user.email,
            noSerialNumber: repairForm.noSerialNumber.value,
            studentId: studentId,
            shippingLocationId: isReplacementNeeded() ? shippingLocationId : null,
        };

        onSubmit(formData);
    };

    return (
        <div>
            <h3>
                Product: {catalogItem.title} | Model: {catalogItem.model}
                {catalogItem.repairCost != null && ` | Repair Cost: $${catalogItem.repairCost.toFixed(2)}`}
            </h3>

            <TextField
                label="Serial Number"
                name="manualSerialNumber"
                value={manualSerialNumber}
                onChange={(e) => setManualSerialNumber(e.target.value)}
                disabled={repairForm.noSerialNumber.value === 'true'} 
                required={repairForm.noSerialNumber.value !== 'true'}
            />

            <CheckBoxField
                name={repairForm.noSerialNumber.name}
                label="No Serial Number or Illegible Serial Number"
                checked={repairForm.noSerialNumber.value === 'true'}
                onChange={handleNoSerialNumberChange}
                helpText="Check this box if the brailler has no serial number or the serial number is illegible"
            />

            <TextArea
                {...repairForm.issue}
                label="Describe the problem"
                rows={4}
            />

            <SelectField
                {...repairForm.replacementPreference}
                label="Replacement Preference"
                helpText="Select the preferred replacement type if the item cannot be repaired"
                options={BRAILLER_REPLACEMENT_STATUS_LIST}
                includeDefaultOption={false}
            />

            {isReplacementNeeded() && shippingLocations && shippingLocations.length > 0 && (
                <>
                    <SelectField
                        label="Shipping Location for Replacement"
                        name="shippingLocationId"
                        value={shippingLocationId || ""}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onShippingLocationChange(e.target.value || null)}
                        options={shippingLocations}
                        includeDefaultOption={true}
                        defaultOption="Select Shipping Location"
                        helpText="Where should the replacement brailler be shipped?"
                    />
                    <LinkButton {...locations.SHIPPING_LOCATION_ADD.link} isExpanded/>
                </>
            )}

            <SelectField
                label="Student"
                name="studentId"
                value={studentId || ""}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onStudentChange(e.target.value)}
                options={students}
                includeDefaultOption={true}
                defaultOption={isFimcAdmin ? "Select a student (optional)" : "Select a student"}
                helpText={isFimcAdmin ? "Optional for FIMC Admin" : "Required"}
            />

            <GridCell className="text-center">
                <Button name="btnSubmitRepairRequest" label="Submit Repair Request" onClick={handleSubmit} isPrimary/>
                <Button name="btnCancelRepairRequest" label="Cancel" onClick={onCancel}/>
            </GridCell>
        </div>
    );
};

